import {
  ImgHTMLAttributes,
  ReactNode,
  Suspense,
  useEffect,
  useRef,
  useState,
  MutableRefObject,
} from "react";
import Skeleton from "react-loading-skeleton";
import { DarkSkeletonTheme, LightSkeletonTheme } from "./skeletons/shared";
import cn from "classnames";

const MIN_SKELETON_MS = 320;

type PageSuspenseProps = {
  fallback: ReactNode;
  children: ReactNode;
};

/**
 * Suspense + gate minimo solo se il chunk non era in cache
 * (cioè solo se Suspense ha davvero mostrato il fallback).
 */
export const PageSuspense = ({ fallback, children }: PageSuspenseProps) => {
  const didSuspend = useRef(false);
  const suspendStartedAt = useRef(0);

  return (
    <Suspense
      fallback={
        <SuspendFallback
          didSuspend={didSuspend}
          suspendStartedAt={suspendStartedAt}
        >
          {fallback}
        </SuspendFallback>
      }
    >
      <MinSkeletonGate
        didSuspend={didSuspend}
        suspendStartedAt={suspendStartedAt}
        fallback={fallback}
      >
        {children}
      </MinSkeletonGate>
    </Suspense>
  );
};

/** Montato solo quando Suspense sospende → marca "cache miss". */
const SuspendFallback = ({
  didSuspend,
  suspendStartedAt,
  children,
}: {
  didSuspend: MutableRefObject<boolean>;
  suspendStartedAt: MutableRefObject<number>;
  children: ReactNode;
}) => {
  didSuspend.current = true;
  if (!suspendStartedAt.current) {
    suspendStartedAt.current = performance.now();
  }
  return <>{children}</>;
};

/**
 * Se non c'è stata sospensione (chunk già in cache) → content subito.
 * Se c'è stata → tiene lo skeleton almeno MIN_SKELETON_MS.
 */
const MinSkeletonGate = ({
  didSuspend,
  suspendStartedAt,
  fallback,
  children,
}: {
  didSuspend: MutableRefObject<boolean>;
  suspendStartedAt: MutableRefObject<number>;
  fallback: ReactNode;
  children: ReactNode;
}) => {
  const suspended = didSuspend.current;
  const [ready, setReady] = useState(!suspended);

  useEffect(() => {
    if (!suspended) return;

    const elapsed = performance.now() - suspendStartedAt.current;
    const wait = Math.max(0, MIN_SKELETON_MS - elapsed);
    const id = window.setTimeout(() => setReady(true), wait);
    return () => window.clearTimeout(id);
  }, [suspended, suspendStartedAt]);

  if (!ready) return <>{fallback}</>;
  return <>{children}</>;
};

type ImageWithSkeletonProps = ImgHTMLAttributes<HTMLImageElement> & {
  /** Tema chiaro (sezioni bianche) */
  light?: boolean;
};

/** Immagine con skeleton che occupa lo stesso slot finché non è caricata.
 *  `width`/`height` HTML = solo aspect-ratio intrinseco (CLS);
 *  le classi Tailwind restano le uniche a governare le dimensioni responsive.
 */
export const ImageWithSkeleton = ({
  className,
  light = false,
  alt = "",
  onLoad,
  onError,
  loading,
  fetchPriority,
  width,
  height,
  ...rest
}: ImageWithSkeletonProps) => {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Default: lazy sotto la fold; eager se esplicitamente richiesto / LCP
  const resolvedLoading =
    loading ??
    (fetchPriority === "high" ? "eager" : "lazy");

  useEffect(() => {
    setLoaded(false);
  }, [rest.src]);

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [rest.src]);

  const themeWrap = (node: ReactNode) =>
    light ? (
      <LightSkeletonTheme>{node}</LightSkeletonTheme>
    ) : (
      <DarkSkeletonTheme>{node}</DarkSkeletonTheme>
    );

  const isAbsolutelyPositioned = Boolean(className?.includes("absolute"));
  const hasHeightClass = Boolean(className?.match(/(?:^|\s)(?:lg:)?h-/));
  const hasWidthClass = Boolean(className?.match(/(?:^|\s)(?:lg:)?w-/));
  const skeletonAspectClass =
    !loaded && hasHeightClass && !hasWidthClass && !(width && height)
      ? "aspect-square"
      : null;
  const skeletonAspectStyle =
    !loaded && hasHeightClass && !hasWidthClass && width && height
      ? { aspectRatio: `${width} / ${height}` }
      : undefined;

  return themeWrap(
    <span
      className={cn(
        "inline-flex items-center justify-center overflow-hidden max-w-full",
        !isAbsolutelyPositioned && "relative",
        skeletonAspectClass,
        className,
      )}
      style={skeletonAspectStyle}
    >
      {!loaded && (
        <Skeleton
          className="!absolute !inset-0 !leading-none !h-full !w-full"
          containerClassName="absolute inset-0 block h-full w-full leading-none"
        />
      )}
      <img
        ref={imgRef}
        alt={alt}
        width={width}
        height={height}
        loading={resolvedLoading}
        fetchPriority={fetchPriority}
        className={cn(
          "object-contain transition-opacity duration-200",
          className?.match(/(?:^|\s)(?:lg:)?h-/)
            ? "h-full w-full max-h-full max-w-full"
            : "w-full h-auto",
          loaded ? "opacity-100" : "opacity-0",
        )}
        onLoad={(e) => {
          setLoaded(true);
          onLoad?.(e);
        }}
        onError={(e) => {
          setLoaded(true);
          onError?.(e);
        }}
        {...rest}
      />
    </span>,
  );
};
